import { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Brain, BarChart3, Target, ArrowLeft, CheckCircle2, XCircle,
  Clock, Star, Flame, ChevronRight, Sparkles, Trophy, HelpCircle,
  TrendingUp, TrendingDown, ChevronDown
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

// Helper to determine the best dataKey for visualization
const getChartDataKey = (data) => {
  if (!data || data.length === 0) return 'clue_count';
  const sample = data[0];
  if (sample.percentage !== undefined) return 'percentage';
  if (sample.contestant_accuracy !== undefined) return 'contestant_accuracy';
  if (sample.clue_count !== undefined) return 'clue_count';
  if (sample.count !== undefined) return 'count';
  if (sample.total !== undefined) return 'total';
  return 'clue_count';
};

// Helper to get the label field (category, answer, or _id)
const getChartLabelKey = (data) => {
  if (!data || data.length === 0) return 'category';
  const sample = data[0];
  if (sample.category !== undefined) return 'category';
  if (sample.answer !== undefined) return 'answer';
  if (sample._id !== undefined) return '_id';
  return 'category';
};

// Helper to format chart data for display
const formatChartData = (data, dataKey) => {
  if (!data) return [];
  const labelKey = getChartLabelKey(data);
  return data.map(item => ({
    ...item,
    category: item[labelKey] || item.category || item.answer || item._id || 'Unknown',
    // Handle contestant_accuracy - check if it's already a percentage (> 1) or decimal (< 1)
    displayValue: dataKey === 'contestant_accuracy' 
      ? (item.contestant_accuracy > 1 ? Math.round(item.contestant_accuracy) : Math.round(item.contestant_accuracy * 100))
      : item[dataKey]
  }));
};

const PHASE = {
  INTRO: 'intro',
  PREDICTION: 'prediction',
  REVEAL: 'reveal',
  VISUALIZATION: 'visualization',
  COMPLETE: 'complete'
};

export default function AskAlexMission() {
  const { missionId } = useParams();
  useSEO('AskAlex Mission | JepPrep', 'Complete AI-powered trivia missions to earn XP and level up your knowledge portfolio.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [mission, setMission] = useState(null);
  const [visualizationData, setVisualizationData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Prediction state
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [currentPredictionIndex, setCurrentPredictionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [predictionResult, setPredictionResult] = useState(null);
  const [allResults, setAllResults] = useState([]);
  const [streak, setStreak] = useState({ current: 0, multiplier: 1.0, label: '' });
  const [statsOpen, setStatsOpen] = useState(false);

  useEffect(() => {
    if (user && missionId) {
      fetchMissionData();
    }
  }, [user, missionId]);

  const fetchMissionData = async () => {
    try {
      setLoading(true);
      const [missionRes, sessionRes] = await Promise.all([
        api.get(`/api/v1/askalex/missions/${missionId}?user_id=${user.id}`),
        api.get('/api/v1/askalex/session')
      ]);
      setMission(missionRes.data);
      setSessionId(sessionRes.data.session_id);
      
      // Pre-fetch visualization data
      const vizRes = await api.get(`/api/v1/askalex/missions/${missionId}/data`);
      setVisualizationData(vizRes.data);
    } catch (error) {
      console.error('Error fetching mission:', error);
    } finally {
      setLoading(false);
    }
  };

  const startMission = () => {
    setPhase(PHASE.PREDICTION);
    setCurrentPredictionIndex(0);
    setAllResults([]);
  };

  const submitPrediction = async () => {
    if (!userAnswer) return;
    
    const prediction = mission.predictions[currentPredictionIndex];
    let answerToSubmit = userAnswer;
    
    // Convert to number for range estimates
    if (prediction.type === 'range_estimate') {
      answerToSubmit = parseFloat(userAnswer);
    }
    
    try {
      const res = await api.post(`/api/v1/askalex/predictions/submit?user_id=${user.id}`, {
        prediction_id: prediction.prediction_id,
        mission_id: missionId,
        session_id: sessionId,
        user_answer: answerToSubmit
      });
      
      setPredictionResult(res.data);
      setStreak({
        current: res.data.current_streak,
        multiplier: res.data.streak_multiplier,
        label: res.data.streak_broken ? 'Streak broken!' : (res.data.current_streak >= 3 ? getStreakLabel(res.data.current_streak) : '')
      });
      setAllResults([...allResults, res.data]);
      setPhase(PHASE.REVEAL);
    } catch (error) {
      console.error('Error submitting prediction:', error);
    }
  };

  const getStreakLabel = (count) => {
    if (count >= 12) return 'Unstoppable!';
    if (count >= 8) return 'Blazing!';
    if (count >= 5) return 'Hot Streak!';
    if (count >= 3) return 'On Fire!';
    return '';
  };

  const nextPrediction = () => {
    setUserAnswer('');
    setPredictionResult(null);
    
    if (currentPredictionIndex < mission.predictions.length - 1) {
      setCurrentPredictionIndex(currentPredictionIndex + 1);
      setPhase(PHASE.PREDICTION);
    } else {
      setPhase(PHASE.VISUALIZATION);
    }
  };

  const completeMission = async () => {
    try {
      await api.post(
        `/api/v1/askalex/missions/${missionId}/complete?user_id=${user.id}&session_id=${sessionId}&time_spent_seconds=300`
      );
      setPhase(PHASE.COMPLETE);
    } catch (error) {
      console.error('Error completing mission:', error);
    }
  };

  const renderPredictionInput = () => {
    const prediction = mission.predictions[currentPredictionIndex];
    
    if (prediction.type === 'multiple_choice' || prediction.type === 'over_under') {
      const options = prediction.options || ['OVER', 'UNDER'];
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {options.map((option) => (
            <Button
              key={option}
              variant={userAnswer === option ? 'default' : 'outline'}
              className={`py-4 sm:py-6 text-sm sm:text-lg whitespace-normal h-auto ${
                userAnswer === option 
                  ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white'
                  : 'border-[hsl(var(--border))] text-white hover:border-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))]'
              }`}
              onClick={() => setUserAnswer(option)}
              data-testid={`option-${option}`}
            >
              {option}
            </Button>
          ))}
        </div>
      );
    }
    
    if (prediction.type === 'range_estimate') {
      return (
        <div className="mt-6">
          <div className="flex items-center gap-3">
            <Input
              type="number"
              placeholder={`Enter your estimate${prediction.unit ? ` (${prediction.unit})` : ''}`}
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-lg py-6"
              data-testid="range-input"
            />
            {prediction.unit && (
              <span className="text-gray-400 text-lg">{prediction.unit}</span>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const renderVisualization = () => {
    if (!visualizationData?.data) return null;
    
    const rawData = visualizationData.data.slice(0, 15);
    const dataKey = getChartDataKey(rawData);
    const labelKey = getChartLabelKey(rawData);
    
    const data = rawData.map(item => ({
      name: (item[labelKey] || item.category || item.answer || item._id || 'Unknown').substring(0, 20),
      value: dataKey === 'contestant_accuracy' 
        ? (item.contestant_accuracy > 1 ? Math.round(item.contestant_accuracy) : Math.round(item.contestant_accuracy * 100))
        : (item[dataKey] || 0),
      rawAccuracy: item.contestant_accuracy
    }));
    
    return (
      <div className="mt-6">
        <h3 className="text-white font-semibold mb-4">{visualizationData.visualization.title}</h3>
        <div className="h-[400px] bg-[hsl(var(--brand-blue-800))] rounded-xl p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30 }}>
              <XAxis type="number" stroke="#6b7280" />
              <YAxis 
                type="category" 
                dataKey="name" 
                stroke="#6b7280" 
                width={120}
                tick={{ fontSize: 11 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(222, 47%, 15%)', 
                  border: '1px solid hsl(222, 47%, 25%)',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value, name) => [
                  dataKey === 'contestant_accuracy' ? `${value}%` : value,
                  dataKey === 'contestant_accuracy' ? 'Accuracy' : 'Count'
                ]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={index < 3 ? 'hsl(45, 93%, 47%)' : 'hsl(217, 91%, 60%)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading mission...</div>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <Card className="p-8 text-center bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <p className="text-white mb-4">Mission not found</p>
          <Link to="/askalex">
            <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
              Back to Missions
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const totalPoints = allResults.reduce((sum, r) => sum + r.points_earned, 0);
  const correctCount = allResults.filter(r => r.is_correct).length;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header - matching SprintStats */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/askalex">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
              <h1 className="text-xl font-display text-white">AskAlex</h1>
            </div>
            
            {/* Progress indicator */}
            {phase !== PHASE.INTRO && phase !== PHASE.COMPLETE && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {mission.predictions.map((_, idx) => (
                    <div 
                      key={`pred-dot-${idx}`}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${
                        idx < currentPredictionIndex 
                          ? 'bg-emerald-500' 
                          : idx === currentPredictionIndex 
                            ? 'bg-[hsl(var(--brand-gold-500))]'
                            : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
                {streak.current >= 3 && (
                  <Badge className="bg-orange-500/20 text-orange-400">
                    <Flame className="w-3 h-3 mr-1" />
                    {streak.current} ({streak.multiplier}x)
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        
        {/* INTRO PHASE */}
        {phase === PHASE.INTRO && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center mx-auto mb-6">
                <Brain className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2">{mission.title}</h1>
              <p className="text-gray-400 mb-6">{mission.description}</p>
              
              <div className="flex items-center justify-center gap-6 mb-8 text-sm">
                <span className="flex items-center gap-2 text-gray-400">
                  <HelpCircle className="w-4 h-4" />
                  {mission.predictions.length} Predictions
                </span>
                <span className="flex items-center gap-2 text-gray-400">
                  <Clock className="w-4 h-4" />
                  {mission.estimated_minutes} min
                </span>
                <span className="flex items-center gap-2 text-[hsl(var(--brand-gold-500))]">
                  <Sparkles className="w-4 h-4" />
                  +{mission.completion_xp} XP
                </span>
              </div>
              
              <Button
                onClick={startMission}
                className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6 text-lg font-semibold"
                data-testid="start-mission-btn"
              >
                Start Mission
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* PREDICTION PHASE */}
        {phase === PHASE.PREDICTION && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <Badge className="bg-[hsl(var(--brand-blue-800))] text-gray-300">
                  Prediction {currentPredictionIndex + 1} of {mission.predictions.length}
                </Badge>
                <span className="text-[hsl(var(--brand-gold-500))] font-medium">
                  +{mission.predictions[currentPredictionIndex].points} pts
                </span>
              </div>
              
              <h2 className="text-xl font-bold text-white mb-2">
                {mission.predictions[currentPredictionIndex].prompt}
              </h2>
              
              {renderPredictionInput()}
              
              <Button
                onClick={submitPrediction}
                disabled={!userAnswer}
                className="w-full mt-6 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold disabled:opacity-50"
                data-testid="submit-prediction-btn"
              >
                Submit Prediction
              </Button>
            </CardContent>
          </Card>
        )}

        {/* REVEAL PHASE */}
        {phase === PHASE.REVEAL && predictionResult && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8">
              {/* Result Header */}
              <div className={`text-center mb-6 p-6 rounded-xl ${
                predictionResult.is_correct 
                  ? 'bg-emerald-500/10 border border-emerald-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  predictionResult.is_correct ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  {predictionResult.is_correct ? (
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-400" />
                  )}
                </div>
                
                <h3 className={`text-2xl font-bold mb-2 ${
                  predictionResult.is_correct ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {predictionResult.is_correct ? 'Correct!' : 'Not Quite'}
                </h3>
                
                <p className="text-white text-lg mb-1">
                  The answer is: <span className="font-bold">{predictionResult.correct_answer}</span>
                </p>
                
                <div className="flex items-center justify-center gap-4 mt-4">
                  <span className="text-[hsl(var(--brand-gold-500))] font-bold text-xl">
                    +{predictionResult.points_earned} pts
                  </span>
                  {predictionResult.streak_multiplier > 1 && (
                    <Badge className="bg-orange-500/20 text-orange-400">
                      {predictionResult.streak_multiplier}x Multiplier
                    </Badge>
                  )}
                </div>
                
                {predictionResult.streak_broken && (
                  <p className="text-red-400 text-sm mt-2">
                    Streak broken! Previous streak: {predictionResult.previous_streak}
                  </p>
                )}
              </div>
              
              {/* Explanation */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-4 mb-6">
                <h4 className="text-white font-medium mb-2">Why?</h4>
                <p className="text-gray-300 text-sm">{predictionResult.explanation}</p>
              </div>
              
              <Button
                onClick={nextPrediction}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                data-testid="next-btn"
              >
                {currentPredictionIndex < mission.predictions.length - 1 
                  ? 'Next Prediction'
                  : 'View Data Insights'
                }
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* VISUALIZATION PHASE */}
        {phase === PHASE.VISUALIZATION && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
                </div>
                <h2 className="text-xl font-bold text-white">Data Insights</h2>
                <p className="text-gray-400">Here's what the data shows</p>
              </div>
              
              {renderVisualization()}
              
              <Button
                onClick={completeMission}
                className="w-full mt-6 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                data-testid="complete-mission-btn"
              >
                Complete Mission
                <Trophy className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* COMPLETE PHASE */}
        {phase === PHASE.COMPLETE && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-10 h-10 text-emerald-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">Mission Complete!</h2>
              <p className="text-gray-400 mb-6">{mission.title}</p>
              
              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <p className="text-[hsl(var(--brand-gold-500))] text-2xl font-bold">{totalPoints}</p>
                    <p className="text-gray-400 text-sm">Points</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <p className="text-emerald-400 text-2xl font-bold">{correctCount}/{mission.predictions.length}</p>
                    <p className="text-gray-400 text-sm">Correct</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <p className="text-cyan-400 text-2xl font-bold">+{mission.completion_xp + totalPoints}</p>
                    <p className="text-gray-400 text-sm">Total XP</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Statistics Dropdown - Only show when all answers are correct */}
              {correctCount === mission.predictions.length && (
                <Collapsible open={statsOpen} onOpenChange={setStatsOpen} className="mb-6">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--brand-blue-800))] justify-between"
                      data-testid="stats-dropdown-trigger"
                    >
                      <span className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                        View Mission Statistics
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${statsOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4">
                    <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                      <CardContent className="p-6 text-left">
                        {/* Visualization Chart */}
                        {visualizationData && (() => {
                          const vizData = visualizationData.data?.slice(0, 10) || [];
                          const dataKey = getChartDataKey(vizData);
                          const chartData = formatChartData(vizData, dataKey);
                          
                          return (
                          <div className="mb-6">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                              <BarChart3 className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                              {visualizationData.visualization?.title || 'Data Insights'}
                            </h3>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                              >
                                <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                                <YAxis 
                                  type="category" 
                                  dataKey="category" 
                                  tick={{ fill: '#fff', fontSize: 11 }}
                                  width={95}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'hsl(222, 47%, 15%)', 
                                    border: '1px solid hsl(222, 47%, 25%)',
                                    borderRadius: '8px'
                                  }}
                                  labelStyle={{ color: '#fff' }}
                                  itemStyle={{ color: '#fff' }}
                                  formatter={(value, name) => [
                                    dataKey === 'contestant_accuracy' ? `${value}%` : value,
                                    dataKey === 'contestant_accuracy' ? 'Accuracy' : name
                                  ]}
                                />
                                <Bar 
                                  dataKey="displayValue"
                                  radius={[0, 4, 4, 0]}
                                >
                                  {chartData.map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={index === 0 ? 'hsl(45, 93%, 47%)' : index < 3 ? 'hsl(45, 70%, 50%)' : 'hsl(222, 47%, 45%)'}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                          );
                        })()}
                      
                      {/* Prediction Results Summary */}
                      <div>
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                          Your Predictions
                        </h3>
                        <div className="space-y-3">
                          {allResults.map((result, idx) => (
                            <div 
                              key={result.label || `result-${idx}`}
                              className={`p-3 rounded-lg border ${
                                result.is_correct 
                                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                                  : 'bg-red-500/10 border-red-500/30'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  result.is_correct ? 'bg-emerald-500' : 'bg-red-500'
                                }`}>
                                  {result.is_correct 
                                    ? <CheckCircle2 className="w-4 h-4 text-white" />
                                    : <XCircle className="w-4 h-4 text-white" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-medium">{mission.predictions[idx]?.prompt}</p>
                                  <div className="flex items-center gap-2 mt-1 text-xs">
                                    <span className="text-gray-400">Your answer:</span>
                                    <span className={result.is_correct ? 'text-emerald-400' : 'text-red-400'}>
                                      {result.user_answer}
                                    </span>
                                    {!result.is_correct && (
                                      <>
                                        <span className="text-gray-600">•</span>
                                        <span className="text-gray-400">Correct:</span>
                                        <span className="text-emerald-400">{result.correct_answer}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <Badge className={`flex-shrink-0 ${
                                  result.is_correct 
                                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                                }`}>
                                  {result.is_correct ? `+${result.points_earned}` : '0'} pts
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>
              )}
              
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/askalex')}
                  variant="outline"
                  className="flex-1 border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--brand-blue-800))] py-6"
                >
                  Back to Missions
                </Button>
                <Button
                  onClick={() => {
                    setPhase(PHASE.INTRO);
                    setAllResults([]);
                    setCurrentPredictionIndex(0);
                    setStatsOpen(false);
                  }}
                  className="flex-1 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
